import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { firstValueFrom, pipe, switchMap, tap } from 'rxjs';
import DataSource from 'devextreme/data/data_source';
import { CustomStore, LoadOptions } from 'devextreme/common/data';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import type {
  Guid,
  LiveQuestionStatusType,
} from './entities/academy-enums.model';
import { LiveRatingEntity } from './entities/academy-entities.model';
import { LiveQuestionResponse } from './entities/academy.model';
import { SubscriberItem } from './entities/academy-responses.model';
import { AcademyODataService } from './infra/academy-odata.service';
import { LiveEventsService } from './infra/live-events.service';
import { LiveParticipantNamesService } from './infra/live-participant-names.service';
import { LiveQuestionsService } from './infra/live-questions.service';

const DETAIL_OPS = ['loadRatings', 'loadTopQuestions'] as const;
/** Grid page size for the server-paged subscribers DataSource. */
const SUBSCRIBERS_PAGE_SIZE = 10;

export interface StarBucket {
  score: number;
  count: number;
  percent: number;
}
export interface ReviewRow {
  personId: Guid;
  name: string | null;
  score: number;
  comment: string;
  addedOnUtc: string | null;
}
export interface TopQuestionRow {
  content: string;
  upvoteCount: number;
  status: LiveQuestionStatusType;
}

interface AcademyEventDetailsState {
  eventId: Guid;
  ratings: LiveRatingEntity[];
  topQuestions: LiveQuestionResponse[];
}

const initialState: AcademyEventDetailsState = {
  eventId: '',
  ratings: [],
  topQuestions: [],
};

const hasComment = (c: string | null): c is string =>
  c != null && c.trim() !== '';

/**
 * Read-only analytics store for the producer "Detalhes do Evento" screen. Subscribers are
 * served to a `lib-dx-data-grid` through a DevExtreme `CustomStore` DataSource
 * (`getSubscribersDataSource`) that pages/searches server-side via the producer endpoint
 * `GET /LiveEvents/{id}/subscribers` — no DX OData store involved. Ratings and questions are
 * event-scoped OData reads with their own req-state slices so the page renders progressively.
 * Header + KPIs are NOT here — the page reads those from `AcademyLivesStore`.
 */
export const AcademyEventDetailsStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyEventDetailsState>(initialState),
  withReqState(...DETAIL_OPS),
  withComputed((store, names = inject(LiveParticipantNamesService)) => ({
    starDistribution: computed<StarBucket[]>(() => {
      const ratings = store.ratings();
      const total = ratings.length;
      return [5, 4, 3, 2, 1].map((score) => {
        const count = ratings.filter((r) => r.Score === score).length;
        return {
          score,
          count,
          percent: total ? Math.round((count / total) * 100) : 0,
        };
      });
    }),
    ratingsTotal: computed<number>(() => store.ratings().length),
    ratingsWithComment: computed<number>(
      () => store.ratings().filter((r) => hasComment(r.Comment)).length
    ),
    reviews: computed<ReviewRow[]>(() => {
      const nameById = names.nameById();
      return store
        .ratings()
        .filter((r) => hasComment(r.Comment))
        .map((r) => ({
          personId: r.PersonId,
          name: nameById[r.PersonId] ?? null,
          score: r.Score,
          comment: r.Comment as string,
          addedOnUtc: r.AddedOnUtc,
        }));
    }),
    topQuestionRows: computed<TopQuestionRow[]>(() =>
      store.topQuestions().map((q) => ({
        content: q.Content,
        upvoteCount: q.UpvoteCount,
        status: q.LiveQuestionStatusType,
      }))
    ),
  })),
  withMethods(
    (
      store,
      odata = inject(AcademyODataService),
      liveEvents = inject(LiveEventsService),
      liveQuestions = inject(LiveQuestionsService),
      names = inject(LiveParticipantNamesService),
      errorStore = inject(ErrorStore)
    ) => {
      /**
       * Builds the server-paged subscribers DataSource for `lib-dx-data-grid`. DX paging
       * (`take`/`skip`) drives `top`/`skip`; the live search box is wired externally and read
       * through `getSearch` so each `reload()` re-queries with the current term. Returns the raw
       * `SubscriberItem` rows (the grid templates read `PersonName`/`TenantName`/`SubscribedOn`).
       */
      const getSubscribersDataSource = (
        eventId: Guid,
        getSearch: () => string = () => ''
      ): DataSource<SubscriberItem> =>
        new DataSource<SubscriberItem>({
          paginate: true,
          pageSize: SUBSCRIBERS_PAGE_SIZE,
          store: new CustomStore<SubscriberItem>({
            key: 'PersonId',
            load: (options: LoadOptions<SubscriberItem>) =>
              firstValueFrom(
                liveEvents.subscribers(eventId, {
                  search: getSearch(),
                  top: options.take,
                  skip: options.skip,
                })
              )
                .then((res) => ({ data: res.items, totalCount: res.total }))
                .catch((error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  return { data: [], totalCount: 0 };
                }),
          }),
        });

      const loadRatings = rxMethod<Guid>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadRatings'))),
          switchMap((id) =>
            odata
              .list<LiveRatingEntity>('LiveRatings', {
                filter: `LiveEventId eq ${id}`,
                orderby: 'AddedOnUtc desc',
              })
              .pipe(
                tapResponse({
                  next: (page) => {
                    names.resolve(page.value.map((r) => r.PersonId));
                    patchState(
                      store,
                      { ratings: page.value },
                      setPropLoaded('loadRatings')
                    );
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('loadRatings', error));
                  },
                })
              )
          )
        )
      );

      // Producer-scoped read: crosses the per-tenant Q&A isolation so the owner sees the
      // audience's questions (client questions on Platform lives). Regular OData
      // (`odata.list('LiveQuestions')`) is tenant-scoped and would hide them. Server orders by
      // upvote desc; we keep the "top 3" highlight of this analytics section.
      const loadTopQuestions = rxMethod<Guid>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadTopQuestions'))),
          switchMap((id) =>
            liveQuestions.listForProducer(id).pipe(
              tapResponse({
                next: (questions) =>
                  patchState(
                    store,
                    {
                      topQuestions: [...questions]
                        .sort((a, b) => b.UpvoteCount - a.UpvoteCount)
                        .slice(0, 3),
                    },
                    setPropLoaded('loadTopQuestions')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadTopQuestions', error));
                },
              })
            )
          )
        )
      );

      const focus = (eventId: Guid): void => {
        patchState(store, { eventId });
        loadRatings(eventId);
        loadTopQuestions(eventId);
      };

      const reset = (): void =>
        patchState(store, initialState, setPropInit(...DETAIL_OPS));

      return {
        getSubscribersDataSource,
        loadRatings,
        loadTopQuestions,
        focus,
        reset,
      };
    }
  )
);
